import React from "react";
import styled from "@emotion/styled";
import { DriveGear, VehicleStatus } from "@optimusride/client-library/@types/grpc/vehicle_status";
import { useTranslation } from "react-i18next";
import { GRAY_900, GRAY_500 } from "../../common/styles/colors";
import { BODY_FONT } from "../../common/styles/fonts";
import { SPACING_16, SPACING_2, SPACING_8 } from "../../common/styles/spacing";
import { VEHICLE_DRIVE_GEAR_STATUS } from "../utils";

const VehicleModalTable = styled.table`
    font-family: ${BODY_FONT};
    overflow: hidden;
`;

const VehicleModalTableHeader = styled.td`
    font-weight: bold;
    padding-right: ${SPACING_16};
`;

const VehicleModalTableValue = styled.td`
    text-transform: uppercase;
`;

const SteeringBar = styled.div`
    position: relative;
    height: ${SPACING_2};
    width: calc(100% - ${SPACING_16});
    background-color: ${GRAY_900};
    border-radius: ${SPACING_16};

    &:after {
        content: "";
        position: absolute;
        top: calc(${SPACING_2} - ${SPACING_8});
        left: calc(${(props: { steering: number }) => props.steering}% - ${SPACING_8});

        display: inline-block;
        height: ${SPACING_16};
        width: ${SPACING_16};

        border-radius: 50%;
        background-color: ${GRAY_500};
    }
`;

export default ({
    vehicleID,
    status,
    className,
}: {
  vehicleID: string;
  status?: VehicleStatus;
  className?: string;
}) => {
    const { t } = useTranslation();

    return (
        <VehicleModalTable className={className}>
            <tbody>
                <tr>
                    <VehicleModalTableHeader>{t("home.vehicleMarker.vehicleName")}</VehicleModalTableHeader>
                    <VehicleModalTableValue>{vehicleID}</VehicleModalTableValue>
                </tr>
                {status && ([
                    [
                        "home.vehicleMarker.gear",
                        t(VEHICLE_DRIVE_GEAR_STATUS[
                            status?.driveGear === undefined ? DriveGear.UNKNOWN : status.driveGear
                        ]),
                    ],
                    [
                        "home.vehicleMarker.speed",
                        status?.speed
                            ? `${Math.round(status.speed)} ${t("home.vehicleStatus.vehicleSpeedUnit")}`
                            : t("home.vehicleStatus.vehicleNASpeed"),
                    ],
                    ["home.vehicleMarker.throttle", `${Math.round((status?.throttle || 0) * 100)}%`],
                    ["home.vehicleMarker.brake", `${Math.round((status?.brake || 0) * 100)}%`],
                    [
                        "home.vehicleMarker.battery",
                        `${
                            (status?.batteryChargePercent && status?.batteryChargePercent >= 0)
                                ? status?.batteryChargePercent
                                : "?"
                        }%`,
                    ],
                ] as [string, string?][]).map(([title, value]) => (value && (
                    <tr key={vehicleID + title}>
                        <VehicleModalTableHeader>{t(title)}</VehicleModalTableHeader>
                        <VehicleModalTableValue>{value}</VehicleModalTableValue>
                    </tr>
                )))}
                {status?.steering && (
                    <tr>
                        <VehicleModalTableHeader>{t("home.vehicleMarker.steering")}</VehicleModalTableHeader>
                        <VehicleModalTableValue>
                            <SteeringBar steering={((status.steering + 1) / 2.0) * 100} />
                        </VehicleModalTableValue>
                    </tr>
                )}
            </tbody>
        </VehicleModalTable>
    );
};
